import styled from "styled-components/macro";
import { brightBlue } from "../../constants/colors";
import { format } from "date-fns";
import { enCA, fr } from "date-fns/locale";
import {
  BREAKPOINT_MOBILE,
  FONT_SIZE_EXTRA_SMALL,
  FONT_SIZE_SMALL,
} from "../../constants/constants";
import { useGetIsOperatingSystemWindows } from "utils/customHooks/useGetIsOperatingSystemWindows";
import {
  FORMAT_READABLE_DATE,
  FORMAT_TIME_12,
  getEpochToReadableTime,
} from "reports/utils/timeUtils";
import i18n from "i18next";

type RavenHeaderStylesType = {
  $shouldUseImage: boolean;
  $isWindows: boolean;
};

const RavenHeaderStyles = styled.div<RavenHeaderStylesType>`
  color: white;

  .secondHeader {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    padding: 10px;
    align-items: center;
    background-color: ${brightBlue};

    .secondHeaderImage {
      display: flex;
      justify-self: flex-start !important;
      padding-left: 20px;
      img {
        width: 52px;
        height: 36px;
      }
    }

    .secondHeaderText {
      grid-column-start: 2;
      text-align: center;

      p {
        padding: 5px 0px;
        font-size: 20px;
        font-family: "GothamLight";
      }

      .subheader {
        font-size: 25px;
        font-family: "GothamBold";
      }
    }

    .secondHeaderDate {
      grid-column-start: 3;
      text-align: right;
      p {
        padding-top: 2em;
        padding-right: 1em;
        font-size: 20px;
        font-family: "GothamLight";
      }
    }
  }

  @media print {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    .secondHeader {
      height: ${props => (props.$isWindows ? 0.9 : 1)}in !important;
      justify-content: center;

      .secondHeaderText {
        p {
          padding: 2px 0px;
        }
      }

      .secondHeaderImage {
        img {
          width: 45px;
          height: auto;
        }
      }

      .secondHeaderDate {
        p {
          font-size: ${FONT_SIZE_EXTRA_SMALL}px !important;
        }
      }
    }
  }

  @media screen and (max-width: ${BREAKPOINT_MOBILE}px) {
    .rightSide {
      p {
        display: none;
      }
    }

    .secondHeader {
      .secondHeaderText {
        margin-left: 1em;
        p {
          font-size: ${FONT_SIZE_EXTRA_SMALL + 2}px !important;
        }

        .subheader {
          font-size: ${FONT_SIZE_SMALL}px !important;
        }
      }
      .secondHeaderDate {
        margin-left: 1em;
        p {
          font-size: ${FONT_SIZE_EXTRA_SMALL}px !important;
        }
      }
      .secondHeaderImage {
        display: flex;
        img {
          width: 40px;
          height: 28px;
        }
      }
    }
  }
`;

/**
 * Get a string representation of the start and end epoch times
 *
 * @param {number} ge
 * @param {number} lt
 * @param {string} pattern (format used by date-fns from timeUtils)
 * @returns
 */
export const getDateStringWithGeAndLt = (
  ge: number,
  lt: number,
  pattern: string
) => {
  const shiftStartFormatted = `${getEpochToReadableTime({
    epochTime: ge,
    formatString: pattern,
  })}`;
  const shiftEndFormatted = `${getEpochToReadableTime({
    epochTime: lt,
    formatString: pattern,
  })}`;
  return `${shiftStartFormatted} - ${shiftEndFormatted}`;
};

/**
 * Get a string representation of the start and end epoch times for EOS reports
 * Gets date string based on whether report range is during DST or not
 *
 * @param {number} ge
 * @param {number} lt
 * @returns string
 */
export const getEosDateStringWithGeAndLt = (ge: number, lt: number) => {
  const locale = { locale: i18n.language === "fr" ? fr : enCA };

  const reportStartTimeZoneOffset =
    new Date(ge).getTimezoneOffset() / 60 - new Date().getTimezoneOffset() / 60;
  const reportEndTimeZoneOffset =
    new Date(lt).getTimezoneOffset() / 60 - new Date().getTimezoneOffset() / 60;

  const reportStart = new Date(ge);
  reportStart.setHours(reportStart.getHours() + reportStartTimeZoneOffset);

  const reportEnd = new Date(lt);
  reportEnd.setHours(reportEnd.getHours() + reportEndTimeZoneOffset);

  const geDate = `${format(reportStart, FORMAT_READABLE_DATE, { ...locale })}`;
  const geTime = `${format(reportStart, FORMAT_TIME_12)}`;

  const ltDate = `${format(reportEnd, FORMAT_READABLE_DATE, { ...locale })}`;
  const ltTime = `${format(reportEnd, FORMAT_TIME_12)}`;

  return geDate === ltDate
    ? `${geDate} ${geTime} - ${ltTime}`
    : `${geDate} ${geTime} - ${ltDate} ${ltTime}`;
};

type ReportHeaderType = {
  title?: string;
  dateString?: string;
  subheader?: string;
  rightSideComponent?: any;
  tenant?: string;
  headerDate?: string;
  shouldUseImage?: boolean;
};

export default function ReportHeader({
  title = "",
  dateString = "",
  subheader = "",
  headerDate = "",
  shouldUseImage = true,
  rightSideComponent,
}: ReportHeaderType) {
  const isWindows = useGetIsOperatingSystemWindows();

  return (
    <div>
      <RavenHeaderStyles
        $shouldUseImage={shouldUseImage}
        $isWindows={isWindows}
      >
        <div className="secondHeader">
          {shouldUseImage && (
            <div className="secondHeaderImage">
              <img src="/images/speedo-white.png" alt="" />
            </div>
          )}
          <div className="secondHeaderText">
            <p>{title}</p>
            <p className="subheader">{subheader}</p>
          </div>
          <div className="secondHeaderDate">
            <p>{headerDate !== "" ? headerDate : dateString}</p>
            {rightSideComponent}
          </div>
        </div>
      </RavenHeaderStyles>
    </div>
  );
}
